import { useEffect, useMemo, useState } from "react";
import { useAuthStore } from "@/features/auth";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import {
  arrayToSingle,
  ALL,
  buildCategoryOptions,
  buildHierarchyOptions,
  buildOrganizationPickerTree,
  collectPickerOptionIds,
  countActiveDashboardScopeFilters,
  getScopedReportingYearOrganizationIds,
  seedDashboardScopeFilters,
  singleToArray,
  scopeOrgTreeByReportingYears,
} from "../utils/dashboard-filter-options";
import {
  buildReportingFrameworkOptions,
  sortReportingYearsDescending,
} from "../utils/reporting-years";

const JJ_GROUP_FRAMEWORK = "JJ-Group";

export const useAssignedMetricsDrawerFilters = ({
  open,
  dashboardFilters,
  available,
  t,
}) => {
  const { user, isJJSEA } = useAuthStore();
  const dashboardSeedSignature = JSON.stringify(
    seedDashboardScopeFilters(dashboardFilters),
  );
  const dashboardSeedFilters = useMemo(
    () => JSON.parse(dashboardSeedSignature),
    [dashboardSeedSignature],
  );
  const [drawerFilters, setDrawerFilters] = useState(
    () => dashboardSeedFilters,
  );

  useEffect(() => {
    if (open) return undefined;

    const timeoutId = window.setTimeout(() => {
      setDrawerFilters((current) => {
        const currentSignature = JSON.stringify(current);
        return currentSignature === dashboardSeedSignature
          ? current
          : dashboardSeedFilters;
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [dashboardSeedFilters, dashboardSeedSignature, open]);

  const yearOptions = useMemo(() => {
    const reportingYearOptions = sortReportingYearsDescending(
      available.reportingYears || [],
    ).map((reportingYear) => ({
      value: String(reportingYear.startYear),
      label: formatReportingYearPeriod(reportingYear),
    }));

    if (reportingYearOptions.length > 0) {
      if (reportingYearOptions.length === 1) return reportingYearOptions;
      return [{ value: ALL, label: t("All Years") }, ...reportingYearOptions];
    }

    const fallbackYearOptions = (available.years || [])
      .slice()
      .sort((left, right) => right - left)
      .map((year) => ({ value: String(year), label: String(year) }));

    if (fallbackYearOptions.length === 1) return fallbackYearOptions;

    return [{ value: ALL, label: t("All Years") }, ...fallbackYearOptions];
  }, [available.reportingYears, available.years, t]);

  const scopedOrgTree = useMemo(
    () =>
      scopeOrgTreeByReportingYears({
        orgTree: available.orgTree || [],
        reportingYears: available.reportingYears || [],
        selectedYears: drawerFilters.years,
        companyId: user?.companyId,
      }),
    [
      available.orgTree,
      available.reportingYears,
      drawerFilters.years,
      user?.companyId,
    ],
  );
  const selectableOrgIds = useMemo(
    () =>
      getScopedReportingYearOrganizationIds({
        reportingYears: available.reportingYears || [],
        selectedYears: drawerFilters.years,
        companyId: user?.companyId,
      }),
    [available.reportingYears, drawerFilters.years, user?.companyId],
  );

  const hierarchyOptions = useMemo(
    () => buildHierarchyOptions(scopedOrgTree, t),
    [scopedOrgTree, t],
  );

  const frameworkOptions = useMemo(() => {
    return buildReportingFrameworkOptions({
      reportingYears: available.reportingYears,
      frameworkObjects: available.frameworkObjects,
      fallbackFrameworks: available.frameworks,
      selectedYears: drawerFilters.years,
      isJJCompany: isJJSEA,
      jjGroupFramework: JJ_GROUP_FRAMEWORK,
      allValue: ALL,
      allLabel: t("All Frameworks"),
      includeAllOption: false,
    });
  }, [
    available.frameworkObjects,
    available.frameworks,
    available.reportingYears,
    drawerFilters.years,
    isJJSEA,
    t,
  ]);

  const categoryOptions = useMemo(
    () => buildCategoryOptions({ user, t }),
    [t, user],
  );

  const activeCount = useMemo(
    () => countActiveDashboardScopeFilters(drawerFilters),
    [drawerFilters],
  );

  const organizationTree = useMemo(
    () =>
      buildOrganizationPickerTree(
        scopedOrgTree,
        drawerFilters.hierarchy || "all",
        selectableOrgIds,
      ),
    [drawerFilters.hierarchy, scopedOrgTree, selectableOrgIds],
  );
  const allowedPickerOrgIds = useMemo(
    () =>
      (drawerFilters.hierarchy || "all") === "all"
        ? selectableOrgIds || []
        : [],
    [drawerFilters.hierarchy, selectableOrgIds],
  );

  useEffect(() => {
    if (
      hierarchyOptions.some(
        (option) => option.value === drawerFilters.hierarchy,
      )
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDrawerFilters((current) => ({
        ...current,
        hierarchy: "all",
        orgIds: [],
      }));
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [drawerFilters.hierarchy, hierarchyOptions]);

  useEffect(() => {
    if (drawerFilters.orgIds.length === 0) return undefined;

    const availableOrgIds = new Set(collectPickerOptionIds(organizationTree));
    const selectableOrgIdSet =
      Array.isArray(allowedPickerOrgIds) && allowedPickerOrgIds.length > 0
        ? new Set(allowedPickerOrgIds.map(String))
        : null;
    if (
      drawerFilters.orgIds.every((id) => {
        const orgId = String(id);
        return (
          availableOrgIds.has(orgId) &&
          (!selectableOrgIdSet || selectableOrgIdSet.has(orgId))
        );
      })
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDrawerFilters((current) => {
        if (current.orgIds.length === 0) return current;
        return {
          ...current,
          orgIds: [],
        };
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [allowedPickerOrgIds, drawerFilters.orgIds, organizationTree]);

  useEffect(() => {
    const realYearOptions = yearOptions.filter(
      (option) => option.value !== ALL,
    );
    if (realYearOptions.length === 1) {
      const onlyYear = Number(realYearOptions[0].value);
      if (
        drawerFilters.years.length === 1 &&
        drawerFilters.years[0] === onlyYear
      ) {
        return undefined;
      }

      const timeoutId = window.setTimeout(() => {
        setDrawerFilters((current) => ({
          ...current,
          years: [onlyYear],
        }));
      }, 0);

      return () => window.clearTimeout(timeoutId);
    }

    if (drawerFilters.years.length === 0) return undefined;

    const availableYears = new Set(
      realYearOptions.map((option) => option.value),
    );
    const selectedYears = drawerFilters.years.map(String);
    if (!selectedYears.every((year) => availableYears.has(year))) {
      const timeoutId = window.setTimeout(() => {
        setDrawerFilters((current) => {
          if (current.years.length === 0) return current;
          return {
            ...current,
            years: [],
          };
        });
      }, 0);

      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [drawerFilters.years, yearOptions]);

  useEffect(() => {
    const realFrameworkOptions = frameworkOptions.filter(
      (option) => option.value !== ALL,
    );

    if (realFrameworkOptions.length === 0) {
      if (drawerFilters.frameworks.length === 0) return undefined;

      const timeoutId = window.setTimeout(() => {
        setDrawerFilters((current) => {
          if (current.frameworks.length === 0) return current;
          return {
            ...current,
            frameworks: [],
          };
        });
      }, 0);

      return () => window.clearTimeout(timeoutId);
    }

    const availableFrameworks = new Set(
      realFrameworkOptions.map((option) => option.value),
    );
    if (
      drawerFilters.frameworks.length === 1 &&
      availableFrameworks.has(drawerFilters.frameworks[0])
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDrawerFilters((current) => ({
        ...current,
        frameworks: [realFrameworkOptions[0].value],
      }));
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [drawerFilters.frameworks, frameworkOptions]);

  const updateFilter = (key, value) => {
    setDrawerFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setDrawerFilters(seedDashboardScopeFilters());
  };

  return {
    drawerFilters,
    activeCount,
    filterOptions: {
      hierarchy: hierarchyOptions,
      organizationTree,
      organizationIds: allowedPickerOrgIds,
      year: yearOptions,
      framework: frameworkOptions,
      category: categoryOptions,
    },
    selectedValues: {
      hierarchy: drawerFilters.hierarchy || "all",
      organizationIds: drawerFilters.orgIds,
      year: arrayToSingle(drawerFilters.years.map(String)),
      framework: drawerFilters.frameworks[0] || "",
      category: arrayToSingle(drawerFilters.categories),
    },
    setHierarchy: (value) =>
      setDrawerFilters((current) => ({
        ...current,
        hierarchy: value || "all",
        orgIds: [],
      })),
    setOrganizationIds: (orgIds) => updateFilter("orgIds", orgIds),
    setYear: (value) => updateFilter("years", singleToArray(value).map(Number)),
    setFramework: (value) => updateFilter("frameworks", value ? [value] : []),
    setCategory: (value) => updateFilter("categories", singleToArray(value)),
    clearFilters,
  };
};
